import { useState, useEffect, useRef } from 'react';
import { trpc } from '../lib/trpc';

type ModalStep = 'setup' | 'loading' | 'results';

interface ManualEvaluationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  userId: string;
  userName?: string | null;
}

interface PreviewResult {
  score: number;
  clampedScore: number;
  feedback: string;
  messageCount: number;
  phaseId: string;
  phaseName: string;
  maxDailyPoints: number;
  sinceDate: string | null;
}

/**
 * Modal component for manually evaluating a user's phase progress.
 *
 * Provides a 2-step process:
 * 1. Setup: Review/override evaluation criteria
 * 2. Results: Display AI analysis and confirm to apply changes
 */
export function ManualEvaluationModal({
  isOpen,
  onClose,
  onSuccess,
  userId,
  userName,
}: ManualEvaluationModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const [step, setStep] = useState<ModalStep>('setup');
  const [useOverride, setUseOverride] = useState(false);
  const [promptOverride, setPromptOverride] = useState('');
  const [previewResult, setPreviewResult] = useState<PreviewResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  // tRPC mutations
  const previewMutation = trpc.users.previewPhaseEvaluation.useMutation();
  const applyMutation = trpc.users.applyPhaseEvaluation.useMutation();

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setStep('setup');
      setUseOverride(false);
      setPromptOverride('');
      setPreviewResult(null);
      setError(null);
    }
  }, [isOpen]);

  // Handle escape key to close modal
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen && step !== 'loading') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, step]);

  // Handle click outside modal to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node) &&
        step !== 'loading'
      ) {
        onClose();
      }
    };

    if (isOpen) {
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 0);
      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen, onClose, step]);

  // Prevent body scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [isOpen]);

  const handlePreview = async () => {
    setStep('loading');
    setError(null);

    try {
      const result = await previewMutation.mutateAsync({
        userId,
        promptOverride: useOverride ? promptOverride : undefined,
      });
      setPreviewResult(result);
      setStep('results');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate preview');
      setStep('setup');
    }
  };

  const handleApply = async () => {
    if (!previewResult) return;

    setError(null);

    try {
      await applyMutation.mutateAsync({
        userId,
        score: previewResult.clampedScore,
      });
      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to apply evaluation');
    }
  };

  const handleBack = () => {
    setStep('setup');
    setError(null);
  };

  if (!isOpen) return null;

  return (
    <div style={styles.overlay}>
      <div ref={modalRef} style={styles.modal}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerLeft}>
            <span style={styles.headerIcon}>
              {step === 'results' ? '📊' : '🎯'}
            </span>
            <div>
              <h2 style={styles.title}>Manual Phase Evaluation</h2>
              <p style={styles.subtitle}>
                {userName || 'User'} {step === 'results' ? '- Results' : '- Setup'}
              </p>
            </div>
          </div>
          {step !== 'loading' && (
            <button
              onClick={onClose}
              style={styles.closeButton}
              aria-label="Close modal"
            >
              x
            </button>
          )}
        </div>

        {/* Content */}
        <div style={styles.content}>
          {/* Error message */}
          {error && (
            <div style={styles.errorMessage}>
              {error}
            </div>
          )}

          {/* Setup Step */}
          {step === 'setup' && (
            <div style={styles.setupContainer}>
              <div style={styles.infoBox}>
                <div style={styles.infoIcon}>i</div>
                <div style={styles.infoContent}>
                  <p style={styles.infoTitle}>What is Manual Evaluation?</p>
                  <p style={styles.infoText}>
                    This will analyze the user's recent messages using AI and calculate a score
                    based on their engagement with the current phase criteria. You can optionally
                    customize the evaluation prompt.
                  </p>
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={useOverride}
                    onChange={(e) => setUseOverride(e.target.checked)}
                    style={styles.checkbox}
                  />
                  <span style={styles.checkboxText}>
                    Override evaluation criteria
                  </span>
                </label>
              </div>

              {useOverride && (
                <div style={styles.formGroup}>
                  <label style={styles.textareaLabel}>
                    Additional Instructions
                  </label>
                  <textarea
                    value={promptOverride}
                    onChange={(e) => setPromptOverride(e.target.value)}
                    placeholder="Enter custom instructions for the evaluation (e.g., 'Focus on positive engagement', 'Be lenient with scoring')..."
                    style={styles.textarea}
                    rows={4}
                  />
                  <p style={styles.helperText}>
                    These instructions will be appended to the default evaluation prompt.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Loading Step */}
          {step === 'loading' && (
            <div style={styles.loadingContainer}>
              <span style={styles.spinner} />
              <p style={styles.loadingText}>Analyzing messages...</p>
              <p style={styles.loadingSubtext}>
                This may take a few seconds while the AI evaluates the user's progress.
              </p>
            </div>
          )}

          {/* Results Step */}
          {step === 'results' && previewResult && (
            <div style={styles.resultsContainer}>
              {/* Phase Info */}
              <div style={styles.phaseBox}>
                <span style={styles.phaseIcon}>📍</span>
                <div style={styles.phaseContent}>
                  <p style={styles.phaseLabel}>Current Phase</p>
                  <p style={styles.phaseName}>{previewResult.phaseName}</p>
                </div>
              </div>

              {/* Stats Grid */}
              <div style={styles.statsGrid}>
                <div style={styles.statCard}>
                  <span style={styles.statIcon}>💬</span>
                  <div>
                    <p style={styles.statValue}>{previewResult.messageCount}</p>
                    <p style={styles.statLabel}>Messages Processed</p>
                  </div>
                </div>
                <div style={styles.statCard}>
                  <span style={styles.statIcon}>⭐</span>
                  <div>
                    <p style={styles.statValue}>
                      {previewResult.clampedScore}
                      <span style={styles.statMax}>/{previewResult.maxDailyPoints}</span>
                    </p>
                    <p style={styles.statLabel}>Score</p>
                  </div>
                </div>
              </div>

              {/* Raw score note if different from clamped */}
              {previewResult.score !== previewResult.clampedScore && (
                <div style={styles.noteBox}>
                  <span style={styles.noteIcon}>*</span>
                  <p style={styles.noteText}>
                    Raw AI score was {previewResult.score}, clamped to max daily points ({previewResult.maxDailyPoints}).
                  </p>
                </div>
              )}

              {/* Since Date */}
              {previewResult.sinceDate && (
                <div style={styles.dateInfo}>
                  <span style={styles.dateLabel}>Messages since:</span>
                  <span style={styles.dateValue}>
                    {new Date(previewResult.sinceDate).toLocaleString()}
                  </span>
                </div>
              )}

              {/* Feedback */}
              <div style={styles.feedbackSection}>
                <h3 style={styles.feedbackTitle}>AI Feedback</h3>
                <div style={styles.feedbackBox}>
                  <p style={styles.feedbackText}>
                    {previewResult.feedback || 'No feedback provided.'}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={styles.footer}>
          {step === 'setup' && (
            <>
              <button
                type="button"
                onClick={onClose}
                style={styles.cancelButton}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handlePreview}
                style={styles.primaryButton}
              >
                Generate Preview
              </button>
            </>
          )}

          {step === 'loading' && (
            <button
              type="button"
              disabled
              style={styles.disabledButton}
            >
              Processing...
            </button>
          )}

          {step === 'results' && (
            <>
              <button
                type="button"
                onClick={handleBack}
                style={styles.cancelButton}
              >
                Back
              </button>
              <button
                type="button"
                onClick={handleApply}
                disabled={applyMutation.isPending}
                style={applyMutation.isPending ? styles.disabledButton : styles.primaryButton}
              >
                {applyMutation.isPending ? 'Applying...' : 'Apply Evaluation'}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Spinner animation */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    width: '100%',
    maxWidth: '520px',
    maxHeight: '85vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: '1px solid rgba(93, 86, 78, 0.08)',
    flexShrink: 0,
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  headerIcon: {
    fontSize: '24px',
  },
  title: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#3D3A36',
    margin: 0,
    fontFamily: '"Sentient", Georgia, serif',
  },
  subtitle: {
    fontSize: '13px',
    color: '#9B9285',
    margin: 0,
  },
  closeButton: {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    border: 'none',
    background: 'rgba(93, 86, 78, 0.06)',
    cursor: 'pointer',
    color: '#706A62',
    fontSize: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background 0.15s ease',
  },
  content: {
    padding: '24px',
    overflowY: 'auto',
    flex: 1,
  },
  errorMessage: {
    padding: '12px 16px',
    backgroundColor: 'rgba(139, 90, 80, 0.1)',
    color: '#8B5A50',
    borderRadius: '10px',
    fontSize: '13px',
    marginBottom: '16px',
  },
  // Setup Step Styles
  setupContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  infoBox: {
    display: 'flex',
    gap: '12px',
    padding: '14px 16px',
    background: 'linear-gradient(135deg, rgba(90, 106, 138, 0.08) 0%, rgba(90, 138, 94, 0.08) 100%)',
    borderRadius: '10px',
    border: '1px solid rgba(90, 106, 138, 0.15)',
  },
  infoIcon: {
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    backgroundColor: 'rgba(90, 106, 138, 0.2)',
    color: '#5A6A8A',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    fontWeight: 600,
    flexShrink: 0,
  },
  infoContent: {
    flex: 1,
  },
  infoTitle: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#3D3A36',
    margin: '0 0 4px 0',
  },
  infoText: {
    fontSize: '13px',
    color: '#706A62',
    lineHeight: '1.5',
    margin: 0,
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    cursor: 'pointer',
  },
  checkbox: {
    width: '18px',
    height: '18px',
    cursor: 'pointer',
    accentColor: '#5A6A8A',
  },
  checkboxText: {
    fontSize: '14px',
    color: '#3D3A36',
    fontWeight: 500,
  },
  textareaLabel: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#5C564E',
  },
  textarea: {
    width: '100%',
    padding: '12px 14px',
    border: '1px solid rgba(93, 86, 78, 0.2)',
    borderRadius: '10px',
    fontSize: '14px',
    fontFamily: 'inherit',
    resize: 'vertical',
    minHeight: '100px',
    outline: 'none',
    transition: 'border-color 0.15s ease',
    boxSizing: 'border-box',
  },
  helperText: {
    fontSize: '12px',
    color: '#9B9285',
    margin: 0,
  },
  // Loading Step Styles
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '48px 24px',
    gap: '16px',
  },
  spinner: {
    width: '32px',
    height: '32px',
    border: '3px solid rgba(93, 86, 78, 0.15)',
    borderTopColor: '#3D3A36',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  loadingText: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#3D3A36',
    margin: 0,
  },
  loadingSubtext: {
    fontSize: '13px',
    color: '#9B9285',
    margin: 0,
    textAlign: 'center',
  },
  // Results Step Styles
  resultsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  phaseBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 14px',
    background: 'rgba(93, 86, 78, 0.03)',
    borderRadius: '10px',
    border: '1px solid rgba(93, 86, 78, 0.08)',
  },
  phaseIcon: {
    fontSize: '18px',
  },
  phaseContent: {
    flex: 1,
  },
  phaseLabel: {
    fontSize: '11px',
    color: '#9B9285',
    margin: 0,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  phaseName: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#3D3A36',
    margin: '2px 0 0 0',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '12px',
  },
  statCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '16px',
    background: 'linear-gradient(135deg, rgba(90, 106, 138, 0.06) 0%, rgba(90, 138, 94, 0.06) 100%)',
    borderRadius: '12px',
    border: '1px solid rgba(90, 106, 138, 0.1)',
  },
  statIcon: {
    fontSize: '20px',
  },
  statValue: {
    fontSize: '22px',
    fontWeight: 700,
    color: '#3D3A36',
    margin: 0,
    lineHeight: 1,
  },
  statMax: {
    fontSize: '14px',
    fontWeight: 500,
    color: '#9B9285',
  },
  statLabel: {
    fontSize: '12px',
    color: '#706A62',
    margin: '4px 0 0 0',
  },
  noteBox: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    padding: '10px 12px',
    background: 'rgba(155, 123, 60, 0.08)',
    borderRadius: '8px',
    border: '1px solid rgba(155, 123, 60, 0.15)',
  },
  noteIcon: {
    fontSize: '12px',
    color: '#9B7B3C',
    fontWeight: 600,
  },
  noteText: {
    fontSize: '12px',
    color: '#7A6330',
    margin: 0,
    lineHeight: 1.4,
  },
  dateInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 12px',
    background: 'rgba(93, 86, 78, 0.03)',
    borderRadius: '8px',
  },
  dateLabel: {
    fontSize: '12px',
    color: '#9B9285',
  },
  dateValue: {
    fontSize: '12px',
    color: '#5C564E',
    fontWeight: 500,
  },
  feedbackSection: {
    marginTop: '4px',
  },
  feedbackTitle: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#5C564E',
    margin: '0 0 10px 0',
  },
  feedbackBox: {
    padding: '14px 16px',
    background: 'rgba(93, 86, 78, 0.03)',
    borderRadius: '10px',
    border: '1px solid rgba(93, 86, 78, 0.08)',
  },
  feedbackText: {
    fontSize: '14px',
    color: '#3D3A36',
    lineHeight: 1.6,
    margin: 0,
    whiteSpace: 'pre-wrap',
  },
  // Footer Styles
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    padding: '16px 24px',
    borderTop: '1px solid rgba(93, 86, 78, 0.08)',
    flexShrink: 0,
  },
  cancelButton: {
    padding: '12px 20px',
    backgroundColor: 'transparent',
    color: '#5C564E',
    border: '1px solid rgba(93, 86, 78, 0.2)',
    borderRadius: '10px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'all 0.15s ease',
  },
  primaryButton: {
    padding: '12px 24px',
    backgroundColor: '#3D3A36',
    color: '#FAF9F7',
    border: 'none',
    borderRadius: '10px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'all 0.15s ease',
  },
  disabledButton: {
    padding: '12px 24px',
    backgroundColor: 'rgba(93, 86, 78, 0.3)',
    color: '#9B9285',
    border: 'none',
    borderRadius: '10px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'not-allowed',
    fontFamily: 'inherit',
  },
};

export default ManualEvaluationModal;
